import React, { memo, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Icon,
  useTheme,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Environment } from '../../environment';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterIcon from '@mui/icons-material/Filter';
import CheckIcon from '@mui/icons-material/Check';

interface IFerramentasDaListagemProps {
  readonly valorDaBusca?: string;
  readonly textoBotaoDeNovo?: string;
  readonly aoClicarEmNovo?: () => void;
  readonly esconderBotaoDeNovo?: boolean;
  readonly esconderCampoDeBusca?: boolean;
  readonly aoMudarBusca?: (valorDaBusca: string) => void;
  readonly AbriModalDeFiltro?: () => void;
  readonly mostrarBotaoDeFiltro?: boolean;
  readonly umItemFiltrado?: boolean;
  readonly doisItemsFiltrados?: boolean;
  readonly tresItemsFiltrados?: boolean;
  readonly quatroItemsFiltrados?: boolean;
  readonly cincoItemsFiltrados?: boolean;
  readonly seisItemsFiltrados?: boolean;
  readonly seteItemsFiltrados?: boolean;
  readonly oitoItemsFiltrados?: boolean;
  readonly noveItemsFiltrados?: boolean;
  readonly maisDeNoveItemsFiltrados?: boolean;
  readonly filtroAtivado?: boolean;
  readonly exportarCSV?: boolean;
  readonly handleExportarCSV?: () => void;
  readonly disableExportarCSV?: boolean;
  readonly erpUsado?: string;
  readonly mostrarBotaoErpUsado?: boolean;
  readonly listaDeFiltros?: readonly string[];
  readonly sincronizarRelatorios?: boolean;
  readonly handleSincronizarRelatorios?: () => void;
  readonly disableSincronizarRelatorios?: boolean;
  readonly ativo?: boolean;
  readonly versionFilter?: string;
  readonly mostrarSomenteAtivosSwitch?: boolean;
  readonly somenteAtivos?: boolean;
  readonly onChangeSomenteAtivos?: (value: boolean) => void;
  readonly mostrarHasDependenciasSwitch?: boolean;
  readonly hasDependencias?: boolean;
  readonly onChangeHasDependencias?: (value: boolean | undefined) => void;
}
// eslint-disable-next-line react/display-name
export const FerramentasDaListagem: React.FC<IFerramentasDaListagemProps> =
  memo(
    ({
      valorDaBusca,
      esconderCampoDeBusca,
      esconderBotaoDeNovo,
      textoBotaoDeNovo = 'Novo',
      aoMudarBusca,
      aoClicarEmNovo,
      AbriModalDeFiltro,
      mostrarBotaoDeFiltro,
      filtroAtivado,
      exportarCSV,
      handleExportarCSV,
      disableExportarCSV,
      erpUsado,
      mostrarBotaoErpUsado,
      listaDeFiltros,
      sincronizarRelatorios,
      disableSincronizarRelatorios,
      handleSincronizarRelatorios,
      ativo,
      versionFilter,
      mostrarSomenteAtivosSwitch,
      somenteAtivos,
      onChangeSomenteAtivos,
      mostrarHasDependenciasSwitch,
      hasDependencias,
      onChangeHasDependencias,
    }) => {
      const theme = useTheme();

      const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          aoMudarBusca?.(e.target.value);
        },
        [aoMudarBusca]
      );

      const listaFiltrada = useCallback(() => {
        return Boolean(
          listaDeFiltros?.length ||
          (erpUsado !== undefined && erpUsado !== 'todos') ||
          ativo !== undefined ||
          versionFilter ||
          filtroAtivado,
        );
      }, [listaDeFiltros, erpUsado, ativo, versionFilter, filtroAtivado]);

      const iconButtonStyles = useMemo(() => ({
        color: theme.palette.primary.contrastText,
        marginLeft: '0.3rem',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: 'transparent',
        },
        '&:active': {
          backgroundColor: 'transparent',
        },
      }), [theme.palette.primary.contrastText]);

      return (
        <>
          <Box flex={1} display="flex" alignItems="center" width="100vw">
            {!esconderCampoDeBusca && (
              <Box display="flex" flexDirection="row" alignItems="center">
                <TextField
                  variant="outlined"
                  value={valorDaBusca}
                  InputLabelProps={{ shrink: true }}
                  placeholder={Environment.INPUT_DE_BUSCA}
                  onChange={handleInputChange}
                  sx={{
                    margin: theme.spacing(0),
                    backgroundColor: theme.palette.background.paper,
                  }}
                />
                {mostrarBotaoDeFiltro && (
                  <Box display="flex" alignItems="center" ml={2}>
                    <IconButton
                      sx={iconButtonStyles}
                      onClick={AbriModalDeFiltro}
                      disableTouchRipple
                      disableFocusRipple
                    >
                      <Box display="flex" alignContent="flex-start" gap={0.5}>
                        <FilterListIcon fontSize="medium" />
                        <FilterIcon
                          sx={{
                            fontSize: '14px',
                            display: listaFiltrada() ? 'flex' : 'none',
                          }}
                        />
                      </Box>
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}

            {mostrarBotaoDeFiltro && esconderCampoDeBusca && (
              <Box
                display="flex"
                alignItems="center"
                // margin={2}
              >
                <IconButton
                  sx={{
                    color: theme.palette.primary.contrastText,
                    marginLeft: '0.3rem',
                  }}
                  onClick={AbriModalDeFiltro}
                >
                  <FilterListIcon />
                </IconButton>
                {filtroAtivado && (
                  <Tooltip title="Filtro ativo" placement="right">
                    <FilterIcon
                      fontSize="small"
                      sx={{
                        color: theme.palette.primary.contrastText,
                        marginLeft: '0.3rem',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {mostrarSomenteAtivosSwitch && (
              <Box display="flex" alignItems="center" ml={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(somenteAtivos)}
                      onChange={(_, checked) =>
                        onChangeSomenteAtivos && onChangeSomenteAtivos(checked)
                      }
                      color="secondary"
                    />
                  }
                  label="Somente ativos"
                  sx={{ color: theme.palette.primary.contrastText }}

                />
              </Box>
            )}
            {mostrarHasDependenciasSwitch && (
              <Box display="flex" alignItems="center" ml={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(hasDependencias)}
                      onChange={(_, checked) =>
                        onChangeHasDependencias && onChangeHasDependencias(checked ? true : undefined)
                      }
                      color="secondary"
                    />
                  }
                  label="Tem dependências"
                  sx={{ color: theme.palette.primary.contrastText }}
                />
              </Box>
            )}
          </Box>
          {!sincronizarRelatorios && (
            <Button
              disabled={esconderBotaoDeNovo}
              onClick={aoClicarEmNovo}
              startIcon={
                <Icon sx={{ display: esconderBotaoDeNovo ? 'none' : 'flex' }}>
                  add
                </Icon>
              }
              sx={{ color: theme.palette.primary.contrastText }}
            >
              {textoBotaoDeNovo}
            </Button>
          )}
          {exportarCSV && (
            <Box width={200} display="flex" justifyContent="center">
              <Button
                disabled={disableExportarCSV}
                variant="contained"
                onClick={handleExportarCSV}
              >
                Exportar CSV
              </Button>
            </Box>
          )}
          {sincronizarRelatorios && (
            <Box width={200} display="flex" justifyContent="center">
              <Button
                disabled={disableSincronizarRelatorios}
                variant="contained"
                onClick={handleSincronizarRelatorios}
                startIcon={<CheckIcon />}
              >
                Sincronizar
              </Button>
            </Box>
          )}
        </>
      );
    },
  );
